/**
 * Available model catalogs for tenant policy (ISS-701).
 *
 * Reuses the tenant-scoped `/api/v1/models/available` endpoint and
 * deduplicates by canonical `catalog_slug` (ADR-041).
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

export interface AvailableCatalog {
  slug: string;
  displayName: string;
}

interface AvailableModel {
  catalog_slug: string;
  display_name?: string;
  model_id?: string;
}

interface AvailableModelsResponse {
  data: Record<string, AvailableModel[]>;
}

export function useAvailableCatalogs() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['llm-config', 'available-catalogs', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<AvailableCatalog[]> => {
      const response = await apiClient.get<AvailableModelsResponse>(
        '/api/v1/models/available',
      );
      const bySlug = new Map<string, AvailableCatalog>();
      for (const models of Object.values(response.data.data ?? {})) {
        for (const model of models) {
          const slug = model.catalog_slug;
          if (!slug) continue;
          bySlug.set(slug, {
            slug,
            displayName: model.display_name || model.model_id || slug,
          });
        }
      }
      return [...bySlug.values()].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}
